'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useBreakpoint } from '@/hooks/useBreakpoint'

function seededRandom(seed: number) {
  const x = Math.sin(seed) * 10000
  return x - Math.floor(x)
}

export default function Sparkles() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isDark, setIsDark] = useState(true)
  const { md } = useBreakpoint()

  useEffect(() => {
    const detectBackground = () => {
      if (!containerRef.current) return

      const parent = containerRef.current.parentElement
      if (!parent) return

      const bgColor = window.getComputedStyle(parent).backgroundColor

      const rgb = bgColor.match(/\d+/g)
      if (rgb && rgb.length >= 3) {
        const [r, g, b] = rgb.map(Number)
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
        setIsDark(luminance < 0.5)
      }
    }

    detectBackground()

    const observer = new MutationObserver(detectBackground)
    if (containerRef.current?.parentElement) {
      observer.observe(containerRef.current.parentElement, {
        attributes: true,
        attributeFilter: ['class', 'style'],
      })
    }

    return () => observer.disconnect()
  }, [])

  const dotColor = isDark ? 'white' : 'black'

  const staticMobileSvg = useMemo(() => {
    const dots = []
    const gridSize = 300
    const spacing = 4

    for (let y = 0; y < gridSize; y += spacing) {
      for (let x = 0; x < gridSize; x += spacing) {
        const seed = x * 1000 + y
        const rand = seededRandom(seed)
        const opacity = (0.1 + rand * 0.4).toFixed(3)
        dots.push(
          `<rect x="${x}" y="${y}" width="1" height="1" fill="${dotColor}" opacity="${opacity}"/>`,
        )
      }
    }

    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300">${dots.join('')}</svg>`.trim()
    return encodeURIComponent(svg)
  }, [dotColor])

  const encodedSvg = useMemo(() => {
    const sharedAnimations = 12
    const dots = []
    const gridSize = 300
    const spacing = 4

    for (let y = 0; y < gridSize; y += spacing) {
      for (let x = 0; x < gridSize; x += spacing) {
        const seed = x * 1000 + y
        const rand1 = seededRandom(seed)
        const rand2 = seededRandom(seed + 1)

        const animIndex = Math.floor(rand2 * sharedAnimations)
        const delay = rand1 * 2

        dots.push({
          x,
          y,
          animIndex,
          delay,
        })
      }
    }

    const svgPattern = `
      <svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300">
        <defs>
          <style>
          @media (prefers-reduced-motion: reduce) {
              ${dots
                .map((dot, i) => {
                  const minOp = (dot.animIndex / sharedAnimations) * 0.15
                  const maxOp = 0.15 + (dot.animIndex / sharedAnimations) * 0.85
                  const staticOp = ((minOp + maxOp) / 2).toFixed(3)
                  return `
                .dot${i} {
                  opacity: ${staticOp};
                }
              `
                })
                .join('')}
            }
            @media (prefers-reduced-motion: no-preference) {
              ${Array.from({ length: sharedAnimations }, (_, i) => {
                const minOp = (i / sharedAnimations) * 0.15
                const maxOp = 0.15 + (i / sharedAnimations) * 0.85
                return `
                @keyframes flicker${i} {
                  0%, 100% { opacity: ${minOp.toFixed(3)}; }
                  50% { opacity: ${maxOp.toFixed(3)}; }
                }
              `
              }).join('')}
              ${dots
                .map(
                  (dot, i) => `
              .dot${i} {
                animation: flicker${dot.animIndex} ${(0.5 + (dot.animIndex / sharedAnimations) * 0.5).toFixed(2)}s ease-in-out ${dot.delay.toFixed(2)}s infinite;
              }
            `,
                )
                .join('')}
            }
          </style>
        </defs>
        ${dots
          .map(
            (dot, i) =>
              `<rect x="${dot.x}" y="${dot.y}" width="1" height="1" fill="${dotColor}" class="dot${i}"/>`,
          )
          .join('')}
      </svg>
    `.trim()

    return encodeURIComponent(svgPattern)
  }, [dotColor])

  const svg = md ? encodedSvg : staticMobileSvg

  return (
    <div
      ref={containerRef}
      data-review="ignore"
      className="meticulous-ignore pointer-events-none absolute inset-0 -z-10"
      style={{
        backgroundImage: `url("data:image/svg+xml,${svg}")`,
        backgroundRepeat: 'repeat',
        backgroundSize: '300px 300px',
        mixBlendMode: 'soft-light',
      }}
    />
  )
}
